import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Search, X, Clock, Tag } from 'lucide-react';
import { mockArticles, miniSimulators, popularTags } from '../data/mock';
import { Link } from 'react-router-dom';

const SearchModal = ({ open, onOpenChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ articles: [], simulators: [] });
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      setIsSearching(true);
      
      // Simuler un délai de recherche
      const timeoutId = setTimeout(() => {
        const query = searchQuery.toLowerCase();
        
        // Recherche dans les articles
        const articleResults = mockArticles.filter(article => 
          article.title.toLowerCase().includes(query) ||
          article.excerpt.toLowerCase().includes(query) ||
          article.tags.some(tag => tag.toLowerCase().includes(query))
        );

        // Recherche dans les simulateurs
        const simulatorResults = miniSimulators.filter(sim =>
          sim.title.toLowerCase().includes(query) ||
          sim.description.toLowerCase().includes(query) ||
          sim.category.toLowerCase().includes(query)
        );

        setSearchResults({
          articles: articleResults.slice(0, 5),
          simulators: simulatorResults.slice(0, 3)
        });
        setIsSearching(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults({ articles: [], simulators: [] });
      setIsSearching(false);
    }
  }, [searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onOpenChange(false);
      // Rediriger vers page de résultats avec query
      window.location.href = `/recherche?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleTagClick = (tag) => {
    setSearchQuery(tag);
  };

  const hasResults = searchResults.articles.length > 0 || searchResults.simulators.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            Rechercher sur le blog
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Barre de recherche */}
          <form onSubmit={handleSearchSubmit}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Rechercher des articles, simulateurs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
                autoFocus
              />
              {searchQuery && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </form>

          {/* Tags populaires */}
          {searchQuery.length === 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Tags populaires :</p>
              <div className="flex flex-wrap gap-2">
                {popularTags.map((tag) => (
                  <Button
                    key={tag}
                    variant="outline"
                    size="sm"
                    onClick={() => handleTagClick(tag)}
                    className="text-xs"
                  >
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Résultats de recherche */}
          {searchQuery.length > 0 && (
            <div className="max-h-96 overflow-y-auto space-y-4">
              {isSearching && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Recherche en cours...</span>
                </div>
              )}

              {!isSearching && !hasResults && searchQuery.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500">Aucun résultat trouvé pour "{searchQuery}"</p>
                </div>
              )}

              {/* Articles */}
              {searchResults.articles.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Articles ({searchResults.articles.length})</h3>
                  <div className="space-y-2">
                    {searchResults.articles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="block p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                        onClick={() => onOpenChange(false)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 text-sm">{article.title}</h4>
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">{article.excerpt}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <Badge variant="secondary" className="text-xs">{article.category}</Badge>
                              <span className="flex items-center text-xs text-gray-500">
                                <Clock className="h-3 w-3 mr-1" />
                                {article.readTime} min
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Simulateurs */}
              {searchResults.simulators.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Simulateurs ({searchResults.simulators.length})</h3>
                  <div className="space-y-2">
                    {searchResults.simulators.map((simulator) => (
                      <Link
                        key={simulator.id}
                        to={`/simulateur/${simulator.id}`}
                        className="block p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                        onClick={() => onOpenChange(false)}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{simulator.icon}</span>
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 text-sm">{simulator.title}</h4>
                            <p className="text-xs text-gray-600">{simulator.description}</p>
                            <Badge variant="outline" className="text-xs mt-1">{simulator.category}</Badge>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;